<template>
  <BaseDialog
    :open="open"
    :disabled="loading"
    max-width="960px"
    @close="onClose"
  >
    <template #title>
      <span>Schüler zuweisen</span>
      <span v-if="group">
        — <strong>{{ group.name }}</strong>
        <span
          v-if="isOwnPrivate"
          class="badge badge-warn"
          title="privat"
          aria-label="privat"
        >privat</span>
      </span>
    </template>

    <div
      class="form"
      style="display:grid;grid-template-columns:1fr 1fr;gap:12px;"
    >
      <!-- Verfügbare Schüler -->
      <div class="field">
        <label for="flt-students">Verfügbare Schüler</label>
        <input
          id="flt-students"
          v-model="studentFilter"
          autocomplete="off"
          placeholder="Filtern (Name/Nummer)"
        >
        <div
          class="chip-box"
          style="margin-top:8px;"
        >
          <div
            class="chips"
            style="display:flex;flex-wrap:wrap;gap:6px;max-height:300px;overflow:auto;"
          >
            <button
              v-for="s in filteredPool"
              :key="s.id"
              type="button"
              class="chip"
              :disabled="loading"
              :title="studentLabel(s)"
              @click="addStudent(s.id)"
            >
              {{ studentLabel(s) }}
            </button>
            <p
              v-if="filteredPool.length===0"
              class="muted"
            >
              Keine Treffer.
            </p>
          </div>
        </div>
        <div
          class="mt-4"
          style="display:flex;justify-content:flex-end;"
        >
          <button
            type="button"
            class="btn btn-primary"
            :disabled="loading || filteredPool.length===0"
            @click="addAllFiltered"
          >
            alle angezeigten Schüler zuweisen
          </button>
        </div>
      </div>

      <!-- Zugewiesene Schüler -->
      <div class="field">
        <label>Zugewiesene Schüler</label>
        <div
          class="chip-box"
          style="margin-top:8px;"
        >
          <div
            class="chips"
            style="display:flex;flex-wrap:wrap;gap:6px;max-height:300px;overflow:auto;"
          >
            <button
              v-for="s in assignedStudents"
              :key="s.id"
              type="button"
              class="chip active"
              :disabled="loading"
              :title="studentLabel(s)"
              @click="removeStudent(s.id)"
            >
              {{ studentLabel(s) }}
            </button>
            <p
              v-if="assignedStudents.length===0"
              class="muted"
            >
              Noch keine Schüler zugewiesen.
            </p>
          </div>
        </div>
        <div
          class="mt-4"
          style="display:flex;justify-content:flex-end;"
        >
          <button
            type="button"
            class="btn btn-warn"
            :disabled="loading || assignedStudents.length===0"
            @click="removeAllAssigned"
          >
            alle Schüler entfernen
          </button>
        </div>
      </div>
    </div>

    <p
      v-if="errorMsg"
      class="notice notice-danger mt-12"
      role="alert"
    >
      {{ errorMsg }}
    </p>

    <div
      class="mt-12"
      style="display:flex;gap:8px;justify-content:flex-end;"
    >
      <button
        class="btn"
        type="button"
        :disabled="loading"
        @click="onClose"
      >
        Schließen
      </button>
    </div>
  </BaseDialog>
</template>

<script setup>
import {computed, onMounted, ref, watch} from 'vue';
import BaseDialog from '@/components/BaseDialog.vue';
import api from '@/services/api';
import {useAuthStore} from '@/stores/auth';

const props = defineProps({
  open: {type: Boolean, default: false},
  group: {type: Object, default: null}
});
const emit = defineEmits(['close', 'applied']);

const auth = useAuthStore();
const isOwnPrivate = computed(() => {
  const g = props.group;
  const uid = auth.user?.id || null;
  return !!g?.is_private && uid != null && Number(g.owner_user_id) === Number(uid);
});

const loading = ref(false);
const errorMsg = ref('');
const studentFilter = ref('');

const allStudents = ref([]);      // alle Schüler
const assignedIds = ref(new Set()); // Set<number>
// IDs, die im Bulk-Add bereits erfolgreich zugewiesen wurden und bis zum Abschluss ausgeblendet werden sollen
const pendingAddIds = ref(new Set()); // Set<number>

const assignedStudents = computed(() => allStudents.value.filter(s => assignedIds.value.has(s.id)));

// Nur aktive Schüler im Pool anzeigen; Zugewiesene bleiben sichtbar (auch wenn inaktiv), um Entfernen zu ermöglichen
const poolStudents = computed(() => allStudents.value.filter(s =>
    !assignedIds.value.has(s.id) &&
    !pendingAddIds.value.has(s.id) &&
    String(s?.status_code ?? '').toLowerCase() === 'aktiv'
));

const filteredPool = computed(() => {
  const q = studentFilter.value.trim().toLowerCase();
  if (!q) return poolStudents.value;
  return poolStudents.value.filter(s => studentLabel(s).toLowerCase().includes(q));
});

function studentLabel(s) {
  const num = s.student_number ? ` · ${s.student_number}` : '';
  const raw = s.class_level ?? null;

  let clsFmt = '';
  if (raw != null && String(raw).trim() !== '') {
    const v = String(raw).trim();

    // Varianten vereinheitlichen → "<zahl>[buchstabe].KL"
    // Beispiele: "1", "1a", "1 KL", "1.kl", "1a-kl" → "1.KL" / "1a.KL"
    const suffixMatch = v.match(/^(\d{1,2}[a-zA-Z]?)\s*[.\- ]?\s*[Kk][Ll]$/);
    if (suffixMatch) {
      clsFmt = `${suffixMatch[1]}.KL`;
    } else {
      const baseMatch = v.match(/^(\d{1,2}[a-zA-Z]?)/);
      if (baseMatch) {
        clsFmt = `${baseMatch[1]}.KL`;
      } else {
        // Fallback: unverändert anzeigen, falls kein übliches Muster erkannt wird
        clsFmt = v;
      }
    }
  }

  const cls = clsFmt ? ` · ${clsFmt}` : '';
  return `${s.last_name}, ${s.first_name}${num}${cls}`;
}

async function loadData() {
  if (!props.group?.id) return;
  loading.value = true;
  errorMsg.value = '';
  try {
    const [{data: st}, {data: mem}] = await Promise.all([
      api.get('/students', {params: {page: 1, limit: 1000}}),
      api.get(`/groups/${props.group.id}/members`)
    ]);
    allStudents.value = Array.isArray(st?.items) ? st.items : [];
    const ids = new Set((Array.isArray(mem?.items) ? mem.items : []).map(s => Number(s.id)));
    assignedIds.value = ids;
  } catch (e) {
    errorMsg.value = e?.response?.data?.error?.message || 'Laden fehlgeschlagen.';
    allStudents.value = [];
    assignedIds.value = new Set();
  } finally {
    loading.value = false;
  }
}

watch(() => props.open, (val) => {
  if (val) {
    loadData();
  } else {
    allStudents.value = [];
    assignedIds.value = new Set();
    studentFilter.value = '';
    errorMsg.value = '';
  }
});

function onClose() {
  emit('close');
}

async function addStudent(studentId) {
  if (!props.group?.id) return;
  loading.value = true;
  try {
    await api.post(`/groups/${props.group.id}/members`, {studentId: Number(studentId)});
    const s = new Set(assignedIds.value);
    s.add(Number(studentId));
    assignedIds.value = s;
    emit('applied');
  } catch (e) {
    errorMsg.value = e?.response?.data?.error?.message || 'Zuweisen fehlgeschlagen.';
  } finally {
    loading.value = false;
  }
}

async function removeStudent(studentId) {
  if (!props.group?.id) return;
  loading.value = true;
  try {
    await api.delete(`/groups/${props.group.id}/members/${Number(studentId)}`);
    const s = new Set(assignedIds.value);
    s.delete(Number(studentId));
    assignedIds.value = s;
    emit('applied');
  } catch (e) {
    errorMsg.value = e?.response?.data?.error?.message || 'Entfernen fehlgeschlagen.';
  } finally {
    loading.value = false;
  }
}

async function addAllFiltered() {
  if (!props.group?.id) return;
  const ids = filteredPool.value.map(s => Number(s.id));
  if (ids.length === 0) return;
  loading.value = true;
  let failed = 0;
  const succeededIds = [];
  try {
    // während des Durchlaufs IDs sukzessive im Pool ausblenden
    const hidden = new Set(pendingAddIds.value);
    for (const id of ids) {
      try {
        await api.post(`/groups/${props.group.id}/members`, {studentId: id});
        hidden.add(id);
        // sofort nach dem erfolgreichen Request den Chip links ausblenden
        pendingAddIds.value = new Set(hidden);
        succeededIds.push(id);
      } catch (e) {
        failed++;
      }
    }
    // am Ende alle erfolgreich zugewiesenen Chips auf einmal sichtbar machen
    if (succeededIds.length > 0) {
      assignedIds.value = new Set([...assignedIds.value, ...succeededIds]);
      emit('applied');
    }
    // temporäre Ausblendungen zurücksetzen
    pendingAddIds.value = new Set();
    if (failed > 0) {
      errorMsg.value = `${failed} von ${ids.length} Zuweisungen fehlgeschlagen.`;
    } else {
      errorMsg.value = '';
    }
  } finally {
    loading.value = false;
  }
}

async function removeAllAssigned() {
  if (!props.group?.id) return;
  const ids = assignedStudents.value.map(s => Number(s.id));
  if (ids.length === 0) return;
  loading.value = true;
  let success = 0;
  let failed = 0;
  try {
    const next = new Set(assignedIds.value);
    for (const id of ids) {
      try {
        await api.delete(`/groups/${props.group.id}/members/${id}`);
        next.delete(id);
        // sofort nach dem erfolgreichen Request den Chip rechts entfernen
        assignedIds.value = new Set(next);
        success++;
      } catch (e) {
        failed++;
      }
    }
    if (success > 0) emit('applied');
    if (failed > 0) {
      errorMsg.value = `${failed} von ${ids.length} Entfernungen fehlgeschlagen.`;
    } else {
      errorMsg.value = '';
    }
  } finally {
    loading.value = false;
  }
}

onMounted(() => {
  if (props.open) loadData();
});
</script>

<style scoped>
.chip-box {
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--surface);
  padding: 8px;
}

.chip {
  border: 1px solid var(--border);
  background: var(--surface);
  color: var(--text);
  border-radius: 999px;
  padding: 4px 10px;
  font-size: 13px;
  line-height: 1.4;
}

.chip.active {
  background: rgba(79, 70, 229, 0.12);
  border-color: var(--primary);
  color: var(--primary-600);
}

.muted {
  color: var(--muted);
}

/* Badge für Kennzeichnungen (z. B. "privat") */
.badge {
  display: inline-block;
  font-size: 12px;
  line-height: 16px;
  padding: 2px 8px;
  border-radius: 999px;
  border: 1px solid var(--border);
  background: var(--surface);
  color: var(--muted);
  margin-left: 8px;
}

.badge-warn {
  color: var(--warn);
  border-color: rgba(245, 158, 11, .5);
  background: rgba(245, 158, 11, .08);
}
</style>
